import React from 'react'
import Link from '../Link/index'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import OracleChatInput from './OracleChatInput'
import { OracleAgentMessage } from './OracleAgentMessage'
import { OracleLoadingSpinner, OracleModerationMessage, ModerationError } from '../OracleCommons'
import { RateLimitState } from './rateLimitUtils'
import { OracleUserMessage } from './OracleUserMessage'
import { isHiddenSyntheticMessage, getUserDisplayText } from './oracleSyntheticMessage'
import { DailyDivinationCard } from './DailyDivinationCard'
import { OracleGreetingCard } from './OracleGreetingCard'
import { PERSONALITY_ARCHITECT_ID } from './personalityFilter'

export interface OracleNewChatProps {
  sendChat: (message: string) => void,
  appendText: string,
  isOperationInProgress: boolean,
  onStop: () => void,
  pendingMessage: string,
  streamingText: string,
  onEnterVoiceMode: () => void,
  rateLimitState?: RateLimitState,
  showRegLink: boolean,
  moderationError?: ModerationError | null,
  onTarotStart: () => void,
  /** Active personality (Architect+ vs Sage) drives placeholder copy and divination visibility */
  activePersonalityId: number,
}

export const OracleNewChat: React.FC<OracleNewChatProps> = ({
  sendChat,
  appendText,
  isOperationInProgress,
  onStop,
  pendingMessage,
  streamingText,
  onEnterVoiceMode,
  rateLimitState,
  showRegLink,
  moderationError,
  onTarotStart,
  activePersonalityId,
}) => {
  const isArchitect = activePersonalityId === PERSONALITY_ARCHITECT_ID
  const chatInputPlaceholder = useTranslatedText({
    keys: ['chatInput', isArchitect ? 'placeholderArchitect' : 'placeholderSage'],
  })
  const architectPromoLinkText = useTranslatedText({ keys: ['architectPromoLink'] })

  return (
    <div className="oracle-new-chat">
      <div className="oracle-new-chat__content">
        <>
          <div className="oracle-new-chat__input-section">
            <OracleGreetingCard sendChat={sendChat}>
              <OracleChatInput
                sendChat={sendChat}
                appendText={appendText}
                placeholder={chatInputPlaceholder}
                isOperationInProgress={isOperationInProgress}
                onStop={onStop}
                pendingMessage={pendingMessage}
                onEnterVoiceMode={onEnterVoiceMode}
                rateLimitState={rateLimitState}
                showPills={false}
                showDisclaimer={false}
              />
            </OracleGreetingCard>
            {!isOperationInProgress && !isArchitect && (
              <DailyDivinationCard onTarotStart={onTarotStart} />
            )}
          </div>
          {showRegLink
            && <div className="oracle-existing-chat__trailer-advertisement">
              <Link
                to={'https://www.gaia.com/lp/getting-to-know-the-architect'}
                target="_blank">{architectPromoLinkText}</Link>
            </div>
          }
        </>

        {isOperationInProgress && (
          <div
            className={`oracle-chatbot-message${
              streamingText ? '' : ' oracle-chatbot-message--initial-loading'
            }`}
          >
            {streamingText ? (
              <OracleAgentMessage text={streamingText} isStreaming />
            ) : (
              <OracleLoadingSpinner />
            )}
          </div>
        )}

        {/* Show moderation error with user message */}
        {moderationError && pendingMessage && !isOperationInProgress && (
          <div className="oracle-new-chat__moderation-error">
            {!isHiddenSyntheticMessage(pendingMessage) && (
              <OracleUserMessage
                text={getUserDisplayText(pendingMessage)}
              />
            )}
            <OracleModerationMessage
              severity={moderationError.config.severity}
              showCrisisResources={moderationError.config.showCrisisResources}
            />
          </div>
        )}
      </div>
    </div>
  )
}
